import {useCallback, useEffect, useRef, useState} from 'react'
import {getSubscriptions} from '../services/subscription'
import {useAuth} from '../modules/auth'
import { useDispatch, useStore } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { setIsSubscription } from '../store/slice/subscription-slice'

const useGetSubscriptionDetails = (): { subscriptionDetails: unknown, subscriptionHandler: () => Promise<unknown> }  => {
  const [subscriptionDetails, setSubscriptionDetails] = useState<any>()
  const {currentUser, setCurrentUser, saveAuth} = useAuth()
  const {id} = currentUser?.data.user
  const dispatch: Dispatch<any> = useDispatch()
  const store = useStore()

  // Keep a live ref to currentUser so subscriptionHandler can be wrapped in a
  // dep-free useCallback. Without this, the handler would either capture a
  // stale user or change identity every render — the latter cascaded into the
  // "Maximum update depth exceeded" loop through react-bootstrap Transitions
  // when the parent (BillingTabs) propagated the new reference into the
  // tab-content tree mid-transition.
  const currentUserRef = useRef(currentUser)
  useEffect(() => {
    currentUserRef.current = currentUser
  }, [currentUser])

  const subscriptionHandler = useCallback(async () => {
    try {
      const response = await getSubscriptions(id)
      const fresh = response.data.data
      setSubscriptionDetails(fresh)
      if (fresh) {
        const latestUser = currentUserRef.current
        const updated = {
          ...latestUser,
          data: {
            ...latestUser.data,
            user: {
              ...latestUser.data.user,
              subscription: fresh,
              ...(fresh?.plan?.name ? {chosenSubscriptionPlan: fresh.plan.name} : {}),
            },
          },
        }
        localStorage.setItem('auth', JSON.stringify(updated))
        setCurrentUser(updated)
        saveAuth(updated)
      }
      if (fresh?.isExpire) {
        setTimeout(() => {
          // Dispatch only when the value would actually change. Pushing the
          // same payload still produces a new state object, re-rendering
          // PrivateRoutes and remounting the route subtree, which can cascade
          // into a "Maximum update depth exceeded" loop through Transitions.
          const current: any = (store.getState() as any)?.subscriptionReducer?.isSubscription
          const currentValue = typeof current === 'string' ? current : current?.isSubscription
          if (currentValue !== 'cancel') {
            dispatch(setIsSubscription({ isSubscription: 'cancel' }))
          }
        }, 3000);
     }
    } catch (error) {
      return error
    }
  }, [id, setCurrentUser, saveAuth, dispatch, store])

  useEffect(() => {
    subscriptionHandler()
    //eslint-disable-next-line
  }, [])

  return {subscriptionDetails, subscriptionHandler}
}

export default useGetSubscriptionDetails
